import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import LogoMark from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          clearCart();
          clearInterval(timer);
        } else if (tries >= 15) {
          setStatus("timeout");
          clearInterval(timer);
        }
      } catch {
        if (tries >= 15) {
          setStatus("error");
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId, clearCart]);

  return (
    <main data-testid="payment-success-page" className="min-h-screen flex items-center justify-center px-5 pt-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-lg w-full text-center border border-white/10 bg-[#111114] p-10 md:p-14"
      >
        <LogoMark className="h-10 w-10 mx-auto mb-8" />
        {status === "checking" && (
          <>
            <Loader2 className="h-10 w-10 text-[#D3FF24] animate-spin mx-auto" />
            <h1 className="font-display font-black text-3xl tracking-tight mt-6">CONFIRMING PAYMENT…</h1>
            <p className="text-sm text-white/50 mt-3">Locking in your order with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-[#D3FF24] mx-auto" data-testid="payment-confirmed-icon" />
            <h1 className="font-display font-black text-4xl tracking-tight mt-6">
              ORDER <span className="text-[#D3FF24]">LOCKED IN.</span>
            </h1>
            <p className="text-sm text-white/50 mt-4 leading-relaxed">
              Payment confirmed. Your gear is being pulled from the racks — a receipt is on its
              way to your inbox from Stripe.
            </p>
            <Link
              to="/shop"
              data-testid="continue-shopping-button"
              className="mt-8 inline-flex items-center justify-center bg-[#D3FF24] text-[#0A0A0C] px-8 py-4 text-xs font-bold tracking-[0.25em] hover:bg-[#BEE61B] transition-colors"
            >
              BACK TO THE ARSENAL
            </Link>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <h1 className="font-display font-black text-3xl tracking-tight mt-6">STILL PROCESSING</h1>
            <p className="text-sm text-white/50 mt-4 leading-relaxed">
              We couldn't confirm the payment just yet. If your card was charged, your order is
              safe — check back shortly.
            </p>
            <Link
              to="/"
              data-testid="back-home-button"
              className="mt-8 inline-flex items-center justify-center border border-white/25 px-8 py-4 text-xs font-bold tracking-[0.25em] hover:border-[#D3FF24] hover:text-[#D3FF24] transition-colors"
            >
              BACK HOME
            </Link>
          </>
        )}
      </motion.div>
    </main>
  );
}
